<template>
  <HomepageWideLayout>
    <SignUpProgress step="2" />
    <b-container class="text-center">
      <h1>Hi {{firstName}}!</h1>

      <b-form class="form text-left" @submit.prevent="onSubmit">
        <p class="form-intro" v-if="!notification">To get the most out of your account please complete the following:</p>
        <b-alert v-else show :variant="notification.type">{{notification.message}}</b-alert>

        <div :class="{error: errors.first('name')}">
          <b-form-input
            v-validate="'required'" name="name"
            v-model="teamUpdates.name"
            class="inp"
            placeholder="Your Company's Name" />
        </div>

        <div v-if="needsDomain" :class="{error: errors.first('domain')}">
          <b-form-input
            v-validate="'required'" name="domain"
            v-model="teamUpdates.domain"
            class="inp"
            placeholder="Your Company's Website" />
        </div>

        <div :class="{error: errors.first('company_size')}">
          <b-form-select
            v-validate="'required'" name="company_size"
            v-model="teamUpdates.company_size"
            class="inp"
            :class="{placeholder: !teamUpdates.company_size}">
            <option :value="null">Your Company Size</option>
            <option>1 or 2</option>
            <option>3+</option>
            <option>10+</option>
            <option>30+</option>
            <option>100+</option>
          </b-form-select>
        </div>

        <div :class="{error: errors.first('job_role')}">
          <b-form-select
            v-validate="'required'" name="job_role"
            v-model="profileUpdates.job_role"
            class="inp"
            :class="{placeholder: !profileUpdates.job_role}">
            <option :value="null">Your Job Role</option>
            <option>Owner/Founder</option>
            <option>Office Manager/Admin</option>
            <option>Designer</option>
            <option>Marketing</option>
            <option>HR</option>
            <option>Sales</option>
            <option>Other</option>
          </b-form-select>
        </div>

        <div :class="{error: errors.first('haveDesign')}">
          <div class="have-design">
            <label>Do you have a business card design?</label>
            <b-form-radio-group
              class="text-center"
              id="haveDesign" name="haveDesign"
              v-validate="'required'"
              v-model="teamUpdates.have_design"
              :options="[{text:'Yes', value:'yes'}, {text:'No', value: 'no'}]"
            />
          </div>
        </div>

        <b-button type="submit" :variant="buttonVariant" :disabled="buttonDisabled" size="lg">
          <WaveSpinner inline color="white" v-if="saving"/>
          <span v-else>Done</span>
        </b-button>

        <pre
        style="text-align: left; font-size: small; border: 3px dashed blue;"
        v-if="flags.debugTools">{{JSON.stringify(errors, null, 4)}}</pre>

      </b-form>
    </b-container>
  </HomepageWideLayout>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import emailDomainType from '@/lib/emailDomainType';
export default {
  meta: {
    requiresAuth: true,
    requiresAuthRedirect: '/signup'
  },
  data() {
    return {
      teamUpdates: {
        domain: null,
        name: '',
        company_size: null,
        have_design: null
      },
      profileUpdates: {
        job_role: null
      },
      saving: false,
      notification: null,
    }
  },
  computed: {
    ...mapGetters(['user', 'flags']),
    firstName() {
      return this.user && this.user.first_name;
    },
    target() {
      // TODO go to a welcome tab
      return '/dashboard';
    },
    pristine() {
      // All fields are pristine
      return Object.values(this.fields).every(field => field.pristine);
    },
    invalid() {
      // Any field is invalid
      return Object.values(this.fields).some(field => field.invalid);
    },
    buttonVariant() {
      // We only switch to the CTA red when the user can submit.
      return !this.pristine && !this.invalid ? 'default' : 'secondary';
    },
    buttonDisabled() {
      // The user can't press the button while the danger message is displaying.
      return this.notification && this.notification.type === 'danger';
    },
    needsDomain() {
      return emailDomainType(this.user.email) !== 'custom';
    }
  },
  watch: {
    invalid(val, old) {
      console.log(val, !!this.notification)
      if (!val && !!this.notification) {
        // User just finished thier job fixing the fields. Encourage them
        // with a nice message
        this.notification = {message: "Thank you! 😀", type: 'success'};
      }
    }
  },
  methods: {
    ...mapActions(['updateTeam', 'updateProfile']),
    async onSubmit() {
      let valid = await this.$validator.validate();
      if (!valid) {
        this.notification = {message: "Please fill in all fields", type: 'danger'};
        return;
      }

      this.saving = true;

      // Treat blank as "No update"
      // This allows user to continue in most cases
      // TODO: remove once we're sure we're not making these optional
      let teamUpdates = {...this.teamUpdates};
      if (!teamUpdates.name) delete teamUpdates.name;
      if (!teamUpdates.domain) delete teamUpdates.domain;
      await Promise.all([
        this.updateTeam(teamUpdates),
        this.updateProfile(this.profileUpdates),
      ]);
      this.saving = false;
      this.$router.push(this.target);
    }
  }
}
</script>


<style scoped lang="scss">
@import '~scss/colors.scss';

.form > div {
  padding: 4px;
  &.error {
    background: #ffc;
  }
}

.signup-intro {
  max-width: 1060px;
  width: 100%;
  padding: 40px 20px;
  margin: 0 auto;
  display: block;
  @media screen and (max-width: 768px) {
    display: none;
  }
}

h1 {
  margin: 0;
  font-size: 2.5rem;
}

h2 {
  color: $gray-0;
  text-transform: uppercase;
  font-weight: normal !important;
  font-size: 0.8rem;
  letter-spacing: 0.1rem;
}

.form {
  margin: 2em auto;
  // This is just wide enough to fit "Do you have a business card design?"
  max-width: 310px;
  > p {
    margin-bottom: 1.5em;
  }
  > .inp {
    margin-bottom: 0.5em;
  }
  .have-design {
    margin: 1em 0;
    label {
      margin: 0;
      padding-bottom: 0.5em;
    }
  }

  .placeholder {
    color: $gray-0;
  }

  button {
    width: 100%;
    &.primary {
      background-color: red;
      border-color: red;
    }
  }

  .skip-link {
    display: block;
    margin: 0.8em;
    color: $gray-0;
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 0.1rem;
  }
}
@import url("https://fonts.googleapis.com/css?family=Roboto");
@import "~scss/social.scss";

</style>



// WEBPACK FOOTER //
// src/components/pages/homepage/Welcome.vue